import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { format } from 'date-fns'
import { useT } from '@/internationalization/i18n'
import type { UsagePoint } from '@/types'

/**
 * Recorded minutes per day.
 *
 * Chart colours come from the same tokens as the rest of the app, read at
 * render time — a hard-coded hex here would stay light-theme blue on a dark
 * background. Every axis label is abbreviated rather than rotated: rotated
 * labels are unreadable at mobile width.
 */
export function UsageChart({ data }: { data: UsagePoint[] }) {
  const t = useT()
  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <defs>
            <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--sb-brand))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--sb-brand))" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="hsl(var(--sb-border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(value: string) => format(new Date(value), 'd MMM')}
            stroke="hsl(var(--sb-fg-subtle))"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis stroke="hsl(var(--sb-fg-subtle))" fontSize={11} tickLine={false} axisLine={false} width={48} />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--sb-surface-raised))',
              border: '1px solid hsl(var(--sb-border))',
              borderRadius: 8,
              fontSize: 12,
              color: 'hsl(var(--sb-fg))',
            }}
            labelFormatter={(value) => format(new Date(String(value)), 'EEE d MMM')}
            formatter={(value: number) => [t('chart.minutes', { value }), t('chart.recorded')]}
          />
          <Area
            type="monotone"
            dataKey="minutes"
            stroke="hsl(var(--sb-brand))"
            strokeWidth={2}
            fill="url(#usageFill)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
